import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { cfString } from '../edge/payload';
import { lookupTlsSignature } from '../data/tlsSignatures';

const FETCH_SOURCE = 'https://fetch.spec.whatwg.org/';

/**
 * Framing / header-normalization probe (desync-cache-fingerprinting §1).
 *
 * IMPORTANT SCOPE NOTE — read before extending this.
 *
 * The source document's headline techniques (CL.TE, TE.CL, 0.CL, malformed
 * Transfer-Encoding) are all "send an ambiguous-framing request and see which
 * header wins". A browser page CANNOT do that: the Fetch standard lists
 * Content-Length, TE, Trailer and Transfer-Encoding as *forbidden request
 * headers*, which the user agent strips before the request is sent. This is a
 * user-agent boundary, not a CORS boundary — no fetch() flag, mode, or
 * credentials setting gets around it. Only raw-socket clients (curl, Python
 * requests with a custom adapter) can emit the ambiguity, and doing so against
 * a third party is an attack technique, not fingerprinting.
 *
 * What IS legitimately observable in-page, and what this probe measures:
 *
 *  1. Enforcement: attempt to attach a forbidden header and confirm it did not
 *     survive. A UA that LETS one through is non-conformant and likely hooked
 *     (anti-detect tooling frequently shims fetch/Headers), which is itself a
 *     strong tampering signal. We never actually transmit an ambiguous
 *     request — we inspect the Headers object before dispatch.
 *  2. Normalization: how the UA lowercases names, trims whitespace, and
 *     rejects CR/LF/NUL in values. A hooked stack often diverges here.
 *  3. Observed framing metadata: what the edge reports about the request it
 *     actually received (Accept-Encoding, and — when the Cloudflare Bot
 *     Management add-on is enabled — true JA4/JA3), plus which intermediary
 *     appears to be in the path (server/via/cf-ray). This is the passive,
 *     non-invasive half of the "identify the proxy in front of the client"
 *     goal, and it reuses the existing edge probe's endpoint.
 *
 * Every value here is a header the browser was going to send anyway; the
 * "framing" values are read, never injected onto the wire.
 */
export const framingProbe: Probe = {
  id: 'framing',
  label: 'Header framing & normalization',
  layer: 'network',
  tier: 'client',
  category: 'browser',
  description: 'Forbidden-header enforcement and UA header normalization behaviour',
  sources: [FETCH_SOURCE],
  budgetMs: 3000,

  async run(): Promise<ProbeResult> {
    const findings: Finding[] = [];
    const endpoint = edgeEndpoint();

    // --- 1. forbidden-header enforcement (measured at dispatch) ----------
    const injected = await attemptForbiddenHeaderInjection(endpoint);
    const survived = injected.survived;
    const enforced = injected.available ? survived.length === 0 : null;

    // Only flag a violation when we actually observed one at the server. If the
    // echo endpoint is unavailable we say "unknown" — we must not report a
    // passing grade we did not measure.
    if (injected.available && survived.length > 0) {
      findings.push({
        id: 'framing.forbidden-header-not-blocked',
        severity: 'high',
        category: 'browser',
        layer: 'network',
        confidence: 'high',
        title: `Forbidden request header reached the server: ${survived.join(', ')}`,
        detail:
          'The Fetch standard requires the user agent to drop Content-Length, Transfer-Encoding, TE, Trailer and the Proxy-/Sec- prefixed headers at dispatch. A request carrying one of these to the server means the fetch stack is hooked or replaced — a common trait of anti-detect browsers and automation wrappers. This is a client-stack integrity check: we sent a single same-origin request with a control header to prove it was really made, and the server simply reported what it received.',
        sources: [FETCH_SOURCE],
        evidence: [
          ...survived.map((h) => `${h} arrived at the server`),
          'control header arrived, so the request was genuinely dispatched',
        ],
        scoreContribution: 30,
      });
    }

    // --- 2. normalization behaviour ---------------------------------------
    const normalization = probeHeaderNormalization();

    const normalizationFindings: Finding[] = [];
    if (normalization.allowsNewline) {
      normalizationFindings.push({
        id: 'framing.newline-accepted-in-header-value',
        severity: 'high',
        category: 'browser',
        layer: 'network',
        confidence: 'high',
        title: 'Header value accepted a newline character',
        detail:
          'A conformant user agent rejects CR/LF in header values (a request-splitting vector). Acceptance indicates a hooked or non-standard Headers implementation.',
        sources: [FETCH_SOURCE],
        evidence: [`CRLF probe stored as ${JSON.stringify(normalization.newlineProbe)}`],
        scoreContribution: 30,
      });
    }
    findings.push(...normalizationFindings);

    // --- 3. observed framing metadata from the edge ------------------------
    // Reuses the /api/edge endpoint; purely a read of what the edge received.
    const observed = await observeEdgeFraming();
    const acceptEncoding = observed.clientAcceptEncoding;

    // zstd in the original Accept-Encoding is a strong "recent Chrome/curl"
    // signal that scripting-language HTTP clients historically lacked.
    if (acceptEncoding) {
      const zstd = /zstd/i.test(acceptEncoding);
      if (!zstd) {
        findings.push({
          id: 'framing.no-zstd-accept-encoding',
          severity: 'info',
          category: 'network',
          layer: 'network',
          confidence: 'low',
          title: 'Accept-Encoding does not advertise zstd',
          detail:
            'zstd support is a strong "recent Chrome/curl" marker; most scripting-language HTTP clients still omit it. Informational only — contributes no score.',
          sources: ['https://www.rfc-editor.org/rfc/rfc8878.html'],
          evidence: [`Accept-Encoding: ${acceptEncoding}`],
          scoreContribution: 0,
        });
      }
    }

    return {
      signals: {
        // Enforcement (null = not measurable without the echo endpoint)
        enforcementMeasured: ok(injected.available, 'derived'),
        forbiddenAttempted: ok(injected.attempted, 'client'),
        forbiddenBlocked: ok(injected.blocked, 'client'),
        forbiddenSurvived: ok(survived, 'client'),
        forbiddenEnforced: ok(enforced, 'derived'),
        // Normalization
        headerCaseNormalized: ok(!normalization.preservesCase, 'derived'),
        crlfRejected: ok(!normalization.allowsNewline, 'derived'),
        whitespaceTrimmed: ok(normalization.whitespaceTrimmed, 'client'),
        // Observed (read-only). Values that can only exist with the paid Bot
        // Management add-on or a populated signature table are reported only
        // when present; constant nulls added nothing on the deployed site.
        clientAcceptEncoding: ok(acceptEncoding, 'edge'),
        intermediary: ok(observed.intermediary, 'edge'),
        ...(observed.ja4 ? { ja4: ok(observed.ja4, 'edge') } : {}),
        ...(observed.ja3 ? { ja3Hash: ok(observed.ja3, 'edge') } : {}),
        ...(observed.signature ? { clientHelloSignature: ok(observed.signature, 'derived') } : {}),
      },
      findings,
      parts: {
        // ALWAYS include the CRLF-rejection result, which we can always
        // measure. The `framingEnforced` value is deliberately NOT included
        // even when measured: it is present only when the echo endpoint is
        // deployed, so including it would make the part differ between a
        // static host and a Worker-backed deployment for a reason that has
        // nothing to do with the device.
        browserFeatures: {
          crlfRejected: !normalization.allowsNewline,
        },
      },
    };
  },
};

/** Same-origin relay, or the configured absolute Worker URL. */
function edgeEndpoint(): string {
  if (typeof location === 'undefined') return '/api/edge';
  const configured = import.meta.env?.VITE_EDGE_ENDPOINT as string | undefined;
  if (configured) return configured;
  return '/api/edge';
}

/**
 * Attempt to attach each forbidden request header to a real request and see
 * whether it reaches the server.
 *
 * IMPORTANT: Headers.set() does NOT apply the forbidden-header filter — the
 * Fetch spec applies it at *dispatch*, so a header can sit in a Headers object
 * and still be stripped before the request goes out. (Verified empirically:
 * every forbidden header "survives" a local Headers.set() in Chromium.) So
 * the only honest way to observe enforcement is to make a real request to an
 * endpoint that reports back what it received, which is what /api/edge does.
 *
 * When no edge endpoint is available we report enforcement as UNKNOWN rather
 * than claiming it passed.
 */
async function attemptForbiddenHeaderInjection(
  endpoint: string,
): Promise<{ available: boolean; attempted: string[]; survived: string[]; blocked: string[] }> {
  const framingNames = ['Content-Length', 'Transfer-Encoding', 'TE', 'Trailer'];
  const prefixNames = ['Proxy-X-Probe', 'Sec-X-Probe'];
  const controlName = 'X-Fpl-Control';

  const headers: Record<string, string> = {};
  for (const n of framingNames) headers[n] = '7';
  for (const n of prefixNames) headers[n] = 'probe';
  // A control header that MUST arrive, proving the request really was made and
  // that reflection works — without it, an empty result would be ambiguous
  // between "stripped" and "request never happened".
  headers[controlName] = 'control-value';

  let received: Record<string, string> | null = null;
  try {
    const res = await fetch(`${endpoint}?hdrprobe=1`, { cache: 'no-store', headers });
    if (res.ok) {
      const json = (await res.json()) as { headers?: Record<string, string> };
      received = json.headers ?? null;
    }
  } catch {
    received = null;
  }

  // No echo, or the control header itself is missing: we learned nothing.
  if (!received || received[controlName.toLowerCase()] !== 'control-value') {
    return { available: false, attempted: [...framingNames, ...prefixNames], survived: [], blocked: [] };
  }

  const survived: string[] = [];
  const blocked: string[] = [];
  for (const n of [...framingNames, ...prefixNames]) {
    const got = received[n.toLowerCase()];
    // We cannot observe the VALUE of a stripped header, only presence. A
    // forbidden header that arrives at all is a conformance failure.
    if (got !== undefined && got !== null) survived.push(n);
    else blocked.push(n);
  }
  return { available: true, attempted: [...framingNames, ...prefixNames], survived, blocked };
}

/** Observe the user agent's header-name/value normalization rules. */
function probeHeaderNormalization(): {
  preservesCase: boolean;
  caseProbe: string;
  allowsNewline: boolean;
  newlineProbe: string | null;
  whitespaceTrimmed: boolean;
} {
  // Case normalization.
  const h1 = new Headers();
  h1.set('X-MiXeD-CaSe', '1');
  const caseProbe = h1.get('x-mixed-case');
  // Headers lookups are case-insensitive, so a conformant UA returns the value
  // regardless of the case we stored. What we actually detect is whether the
  // stored NAME kept its case when iterated.
  let preservesCase = false;
  for (const [k] of h1) {
    if (/[A-Z]/.test(k)) preservesCase = true;
  }

  // CR/LF in a value. Per spec this must be rejected (Headers.set throws or
  // the value is stripped). We record the stored value to distinguish.
  let newlineProbe: string | null = null;
  let allowsNewline = false;
  try {
    const h2 = new Headers();
    h2.set('X-Probe', 'a\r\nb');
    newlineProbe = h2.get('X-Probe');
    // A conformant value has no CR/LF; if it round-trips with them, the UA
    // accepted an injection-prone value.
    if (newlineProbe !== null && /[\r\n]/.test(newlineProbe)) allowsNewline = true;
  } catch {
    // Throwing is conformant; leave allowsNewline false.
  }

  // Leading/trailing whitespace must be stripped from values.
  let whitespaceTrimmed = false;
  try {
    const h3 = new Headers();
    h3.set('X-Probe', '  padded  ');
    whitespaceTrimmed = h3.get('X-Probe') === 'padded';
  } catch {
    whitespaceTrimmed = false;
  }

  return { preservesCase, caseProbe: caseProbe ?? 'null', allowsNewline, newlineProbe, whitespaceTrimmed };
}

/** Read framing metadata the edge already exposes. No injection, read-only. */
async function observeEdgeFraming(): Promise<{
  clientAcceptEncoding: string | null;
  intermediary: string | null;
  ja4: string | null;
  ja3: string | null;
  signature: string | null;
}> {
  const empty = {
    clientAcceptEncoding: null,
    intermediary: null,
    ja4: null,
    ja3: null,
    signature: null,
  };
  const endpoint = edgeEndpoint();
  try {
    const res = await fetch(endpoint, { cache: 'no-store' });
    if (!res.ok) return empty;
    const payload = (await res.json()) as {
      cf: Record<string, unknown> | null;
      botManagement: { ja4?: string; ja3Hash?: string } | null;
      clientAcceptEncoding: string | null;
    };
    const cf = payload.cf ?? {};
    const bm = payload.botManagement ?? null;
    const ciphers = cfString(cf, 'tlsClientCiphersSha1') ?? undefined;
    const exts = cfString(cf, 'tlsClientExtensionsSha1') ?? undefined;
    const sig = lookupTlsSignature(ciphers, exts);
    return {
      clientAcceptEncoding: payload.clientAcceptEncoding ?? null,
      intermediary: cfString(cf, 'asOrganization') ?? cfString(cf, 'colo'),
      ja4: bm?.ja4 ?? null,
      ja3: bm?.ja3Hash ?? null,
      signature: sig?.value.label ?? null,
    };
  } catch {
    return empty;
  }
}

registerProbe(framingProbe);
